import * as React from 'react';
import { responsiveFontSize, responsiveIconSize, fontSizeFixed } from '../../util/responsiveUtils';
import { Box, Button, IconButton, Stack, Typography, Avatar, Menu } from '@mui/material';
import HandymanIcon from '@mui/icons-material/Handyman';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HistoryIcon from '@mui/icons-material/History';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LoginIcon from '@mui/icons-material/Login';
import WorkIcon from '@mui/icons-material/Work';
import { useNavigate } from 'react-router';
import { ColorSchemeMenuToggle } from './ColorSchemeToggle';
import { NotificationIcon } from './NotificationIcon';
import { useAuth } from '../../context';
const ServicesMenu = React.lazy(() =>
    import('../services/ServicesMenu').then((m) => ({ default: m.ServicesMenu })),
);
const ThirdLevelMenu = React.lazy(() =>
    import('../services/ServicesMenu').then((m) => ({ default: m.ThirdLevelMenu })),
);
import { SearchBar } from './SearchBar';
import { buildApiUrl } from '../../config/api';
import { Z_INDEX } from '../../constants/zIndex';
import { scheduleDeferred } from '../../util/scheduleDeferred';
import { useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getButtonColor, getButtonHoverColor } from '../../theme/themeUtils';
import { VirtolyLogo } from '../common/VirtolyLogo';
import { HeaderHelpTrigger, useNeedHelpFlow } from './HeaderHelpButton';

const AuthModal = React.lazy(() =>
    import('../loginAndAuth/AuthModal').then((m) => ({ default: m.AuthModal })),
);
const GetStartedChooser = React.lazy(() =>
    import('../loginAndAuth/GetStartedChooser').then((m) => ({ default: m.GetStartedChooser })),
);
const CitySelectionModal = React.lazy(() =>
    import('../CitySelectionModal').then((m) => ({ default: m.CitySelectionModal })),
);

/** Reusable dashboard-style menu item used in all header menus */
interface DashboardMenuItemProps {
    icon: React.ReactNode;
    text: string;
    onClick: (...args: any[]) => void;
    isLogout?: boolean;
    hasArrow?: boolean;
    showDivider?: boolean;
    isProminent?: boolean;
}
const DashboardMenuItem: React.FC<DashboardMenuItemProps & { theme: any }> = ({ icon, text, onClick, isLogout, hasArrow, showDivider = true, isProminent = false, theme }) => (
    <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            minHeight: 44,
            py: isProminent ? { xs: 1.5, sm: 2 } : { xs: 1.25, sm: 1.5 },
            px: { xs: 2, sm: 2.5 },
            width: '100%',
            border: 'none',
            background: isProminent ? alpha(getButtonColor(theme), 0.1) : 'none',
            cursor: 'pointer',
            textAlign: 'left',
            font: 'inherit',
            color: 'inherit',
            transition: 'all 0.2s ease',
            borderBottom: showDivider ? `1px solid ${theme.palette.divider}` : 'none',
            ...(isProminent && {
                mb: 0.5,
                borderRadius: 1,
                mx: 0.5,
                mt: 0.5,
                borderBottom: 'none',
                '& + *': { borderTop: `1px solid ${theme.palette.divider}` }
            }),
            '@media (hover: hover)': {
                '&:hover': {
                    backgroundColor: isProminent ? alpha(getButtonColor(theme), 0.16) : alpha(getButtonColor(theme), 0.06),
                }
            },
            '&:focus-visible': {
                outline: `2px solid ${getButtonColor(theme)}`,
                outlineOffset: 2
            }
        }}
    >
        <Box
            sx={{
                width: isProminent ? 40 : 36,
                height: isProminent ? 40 : 36,
                borderRadius: 2,
                backgroundColor: isLogout ? alpha(theme.palette.error.main, 0.12) : alpha(getButtonColor(theme), isProminent ? 0.2 : 0.12),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
            }}
        >
            <Box sx={{ color: isLogout ? theme.palette.error.main : getButtonColor(theme), display: 'flex', alignItems: 'center', justifyContent: 'center', '& .MuiSvgIcon-root': { fontSize: isProminent ? 22 : 20 } }}>
                {icon}
            </Box>
        </Box>
        <Typography
            sx={{
                flex: 1,
                fontWeight: isProminent ? 600 : 500,
                fontSize: isProminent ? responsiveFontSize.bodyMenuTo1 : responsiveFontSize.bodyMenu,
                color: isLogout ? 'error.main' : 'text.primary'
            }}
        >
            {text}
        </Typography>
        {hasArrow && <ArrowRightIcon sx={{ color: theme.palette.action.active, fontSize: fontSizeFixed.rem125, flexShrink: 0 }} />}
    </Box>
);

export function HeaderComponent() {
    const theme = useTheme();
    /** Inline search needs enough width so it does not overlap centered Virtoly (overlap occurs roughly md–1033px). */
    const showInlineSearch = useMediaQuery('(min-width:1034px)');
    const isNarrowViewport = useMediaQuery(theme.breakpoints.down('md'));
    const auth = useAuth();
    const [hamburgerMenuAnchor, setHamburgerMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [getStartedOpen, setGetStartedOpen] = React.useState(false);
    const [authModalOpen, setAuthModalOpen] = React.useState(false);
    const [authModalMode, setAuthModalMode] = React.useState<'login' | 'signup'>('signup');
    const [authModalLoginOnly, setAuthModalLoginOnly] = React.useState(false);
    const [authModalExpectedUserId, setAuthModalExpectedUserId] = React.useState<number | undefined>(undefined);
    const [authModalCustomMessage, setAuthModalCustomMessage] = React.useState<string | undefined>(undefined);
    const [userType, setUserType] = React.useState<'customer' | 'worker' | 'admin' | 'manager' | null>(
        auth?.credentialType === 1 ? 'customer' : auth?.credentialType === 3 ? 'admin' : auth?.credentialType === 4 ? 'manager' : auth.credentialType === 2 ? 'worker' : null
    );
    const [subMenuAnchor, setSubMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [thirdLevelMenuAnchor, setThirdLevelMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [activeService, setActiveService] = React.useState<string | null>(null);
    const [profilePicture, setProfilePicture] = React.useState<string | null>(null);
    const [cityModalOpen, setCityModalOpen] = React.useState(false);
    const [pendingSearchAfterCity, setPendingSearchAfterCity] = React.useState<string | null>(null);
    const [shellModalsReady, setShellModalsReady] = React.useState(false);
    const navigate = useNavigate();
    const { openNeedHelp, needHelpModals } = useNeedHelpFlow();

    React.useEffect(() => {
        scheduleDeferred(() => setShellModalsReady(true));
    }, []);

    const prepareShellModals = React.useCallback(() => {
        setShellModalsReady(true);
    }, []);

    React.useEffect(() => {
        let type: 'customer' | 'worker' | 'admin' | 'manager' | null = null;
        
        // Try to determine from auth.credentialType (number)
        if (auth?.credentialType) {
            if (auth.credentialType === 1) type = 'customer';
            else if (auth.credentialType === 2) type = 'worker';
            else if (auth.credentialType === 3) type = 'admin';
            else if (auth.credentialType === 4) type = 'manager';
        }
        
        // If not found, try from user.credential_type (string or number)
        if (!type && auth?.user?.credential_type) {
            const userCredType = auth.user.credential_type;
            
            if (typeof userCredType === 'string') {
                if (userCredType === 'customer') type = 'customer';
                else if (userCredType === 'worker') type = 'worker';
                else if (userCredType === 'admin') type = 'admin';
                else if (userCredType === 'manager') type = 'manager';
            } else if (Array.isArray(userCredType)) {
                if (userCredType.includes(1) || userCredType.includes('customer')) type = 'customer';
                else if (userCredType.includes(2) || userCredType.includes('worker')) type = 'worker';
                else if (userCredType.includes(3) || userCredType.includes('admin')) type = 'admin';
                else if (userCredType.includes(4) || userCredType.includes('manager')) type = 'manager';
            } else if (typeof userCredType === 'number') {
                if (userCredType === 1) type = 'customer';
                else if (userCredType === 2) type = 'worker';
                else if (userCredType === 3) type = 'admin';
                else if (userCredType === 4) type = 'manager';
            }
        }
        
        setUserType(type);
    }, [auth.credentialType, auth.user?.credential_type]);

    // Fetch profile picture
    const fetchProfilePicture = React.useCallback(async () => {
        if (!auth.token || !auth.user?.id) return;
        
        try {
            const response = await fetch(buildApiUrl(`/api/images?type=profile&entity_id=${auth.user.id}`), {
                headers: {
                    'Authorization': `Bearer ${auth.token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data && data.data.length > 0) {
                    // Get the primary image or the first one
                    const primaryImage = data.data.find((img: any) => img.is_primary) || data.data[0];
                    setProfilePicture(primaryImage.url);
                } else {
                    setProfilePicture(null);
                }
            }
        } catch (error) {
            console.error('Failed to fetch profile picture:', error);
            setProfilePicture(null);
        }
    }, [auth.token, auth.user?.id]);

    React.useEffect(() => {
        if (auth.isLogin()) {
            fetchProfilePicture();
        }
    }, [auth.token, auth.user?.id, auth.isLogin(), fetchProfilePicture]);

    // Listen for profile picture updates
    React.useEffect(() => {
        const handleProfilePictureUpdate = () => {
            fetchProfilePicture();
        };

        window.addEventListener('profilePictureUpdated', handleProfilePictureUpdate);
        
        return () => {
            window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdate);
        };
    }, [fetchProfilePicture]);

    // Listen for openAuthModal events (e.g., from pages that require login)
    React.useEffect(() => {
        const handleOpenAuthModal = (event: CustomEvent) => {
            const { type, mode, loginOnly, expectedUserId, customMessage } = event.detail || {};
            // Default to customer if no type specified
            setUserType(type || 'customer');
            // Set initial mode (default to signup unless specified)
            setAuthModalMode(mode || 'signup');
            // Set loginOnly mode (disables signup option for email notification links)
            setAuthModalLoginOnly(loginOnly || false);
            // Set expected user ID for credential verification
            setAuthModalExpectedUserId(expectedUserId);
            // Set custom message to display in modal
            setAuthModalCustomMessage(customMessage);
            prepareShellModals();
            setAuthModalOpen(true);
        };

        window.addEventListener('openAuthModal', handleOpenAuthModal as EventListener);
        
        return () => {
            window.removeEventListener('openAuthModal', handleOpenAuthModal as EventListener);
        };
    }, []);

    // Listen for openCityModal events (e.g. from SearchBar or WorkersInCity when location not selected)
    React.useEffect(() => {
        const handleOpenCityModal = (event: CustomEvent<{ pendingSearch?: string }>) => {
            const pendingSearch = event.detail?.pendingSearch ?? null;
            setPendingSearchAfterCity(pendingSearch);
            prepareShellModals();
            setCityModalOpen(true);
        };

        window.addEventListener('openCityModal', handleOpenCityModal as EventListener);

        return () => {
            window.removeEventListener('openCityModal', handleOpenCityModal as EventListener);
        };
    }, []);

    const handleCityModalClose = React.useCallback(() => {
        setCityModalOpen(false);
        setPendingSearchAfterCity(null);
    }, []);

    const handleCitySelected = React.useCallback(() => {
        if (pendingSearchAfterCity) {
            navigate(`/find-experts-in-city?search=${encodeURIComponent(pendingSearchAfterCity)}`);
        }
        setCityModalOpen(false);
        setPendingSearchAfterCity(null);
    }, [navigate, pendingSearchAfterCity]);

    const onClickLogout = React.useCallback(() => {
        auth.logout();
        navigate("/");
    }, [auth, navigate]);

    const onClickCustomerDashboard = React.useCallback(() => {
        navigate("/customer-dashboard");
    }, [navigate]);

    const onClickCustomerCreateOrder = React.useCallback(() => {
        navigate("/customer-create-order");
    }, [navigate]);

    const onClickCustomerOrderHistory = React.useCallback(() => {
        navigate("/customer-order-history");
    }, [navigate]);

    const onClickWorkerDashboard = React.useCallback(() => {
        navigate("/expert-dashboard");
    }, [navigate]);

    const onClickWorkerJobHistory = React.useCallback(() => {
        navigate("/expert-job-history");
    }, [navigate]);

    const onClickAvailableProjects = React.useCallback(() => {
        navigate("/available-projects");
    }, [navigate]);

    const onClickAdminDashboard = React.useCallback(() => {
        navigate("/admin/dashboard");
    }, [navigate]);

    const onClickAdminProfile = React.useCallback(() => {
        navigate('/admin-profile');
    }, [navigate]);

    const onClickManagerDashboard = React.useCallback(() => {
        navigate("/manager/dashboard");
    }, [navigate]);

    const onClickManagerProfile = React.useCallback(() => {
        navigate('/manager-profile');
    }, [navigate]);

    const onClickServices = React.useCallback(() => {
        // Services menu is handled by ServicesMenu component - no navigation needed
        closeAllMenus();
    }, []);

    const onClickFindWorkers = React.useCallback(() => {
        navigate("/find-experts-in-city");
    }, [navigate]);

    const onClickTerms = React.useCallback(() => {
        navigate("/terms");
    }, [navigate]);

    const onClickNotifications = React.useCallback(() => {
        navigate("/notifications");
    }, [navigate]);

    const handleHamburgerMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setHamburgerMenuAnchor(event.currentTarget);
    };

    const closeAllMenus = () => {
        setHamburgerMenuAnchor(null);
        setSubMenuAnchor(null);
        setThirdLevelMenuAnchor(null);
        setActiveService(null);
    };

    const showAuthOptions = () => {
        closeAllMenus();
        prepareShellModals();
        setGetStartedOpen(true);
    };

    const handleAuthSelection = (type: 'customer' | 'worker') => {
        setUserType(type);
        // Reset modal state for normal login/signup flow
        setAuthModalMode('signup');
        setAuthModalLoginOnly(false);
        setAuthModalExpectedUserId(undefined);
        prepareShellModals();
        setAuthModalOpen(true);
        closeAllMenus();
    };

    const handleSubMenuOpen = (event: React.MouseEvent<HTMLElement>, service: string) => {
        setSubMenuAnchor(event.currentTarget);
        setActiveService(service);
    };

    const handleThirdLevelMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setThirdLevelMenuAnchor(event.currentTarget);
    };

    /** Close subcategory menu only; keep the category list (Services menu) open. */
    const handleCloseThirdLevelOnly = React.useCallback(() => {
        setThirdLevelMenuAnchor(null);
        setActiveService(null);
    }, []);

    /** Close the services category submenu only; keep the hamburger menu open. */
    const handleCloseServicesMenuOnly = React.useCallback(() => {
        setSubMenuAnchor(null);
        setThirdLevelMenuAnchor(null);
        setActiveService(null);
    }, []);

    const handleMenuItemClick = (handler: () => void) => {
        handler();
        closeAllMenus();
    };

    const handleSubMenuClick = (e: React.MouseEvent<HTMLElement>, service: string) => {
        handleSubMenuOpen(e, service);
        // Don't close here as we want the submenu to open
    };

    return (
        <Box id="app-header" sx={{
            width: '100%',
            backgroundColor: 'background.paper',
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            zIndex: Z_INDEX.HEADER,
            boxShadow: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
        }}>
            <Box sx={{
                display: 'grid',
                width: '100%',
                maxWidth: { xs: '100%', sm: 'lg' },
                mx: 'auto',
                py: { xs: 0.5, sm: 1 },
                px: { xs: 1, sm: 2, md: 3, lg: 4 },
                alignItems: 'center',
                columnGap: { xs: 0.75, sm: 1.5, md: 2 },
                rowGap: showInlineSearch ? 0 : 1,
                gridTemplateColumns: {
                    xs: '1fr auto 1fr',
                    md: '1fr auto 1fr',
                },
                gridTemplateRows: showInlineSearch ? 'auto' : 'auto auto',
            }}>
                {/* Help — left */}
                <Box
                    sx={{
                        gridColumn: { xs: '1', md: '1' },
                        gridRow: { xs: '1', md: '1' },
                        justifySelf: 'start',
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0,
                    }}
                >
                    <HeaderHelpTrigger onClick={openNeedHelp} />
                </Box>

                {/* Brand — centered (logo + wordmark) */}
                <Stack
                    direction="row"
                    component="button"
                    type="button"
                    onClick={() => navigate('/')}
                    alignItems="center"
                    justifyContent="center"
                    aria-label="Virtoly home"
                    sx={{
                        gridColumn: { xs: '2', md: '2' },
                        gridRow: { xs: '1', md: '1' },
                        justifySelf: 'center',
                        m: 0,
                        p: 0,
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: 'inherit',
                        font: 'inherit',
                        gap: { xs: 0.75, sm: 1, md: 1.25 },
                        flexShrink: 0,
                        '@media (hover: hover)': {
                            '&:hover': { opacity: 0.92 },
                        },
                        '&:focus-visible': {
                            outline: `2px solid ${getButtonColor(theme)}`,
                            outlineOffset: 2,
                            borderRadius: 1,
                        },
                    }}
                >
                    <VirtolyLogo alt="Virtoly" />
                    <Typography variant="subtitle1" component="span" sx={{ whiteSpace: 'nowrap' }}>
                        Virtoly
                    </Typography>
                </Stack>

                {/* Below ~1034px: search on second row so it does not cover Virtoly */}
                {!showInlineSearch && (
                    <Box
                        sx={{
                            gridColumn: '1 / -1',
                            gridRow: 2,
                            width: '100%',
                            minWidth: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                width: '95%',
                                maxWidth: 520,
                                minWidth: 0,
                            }}
                        >
                            <SearchBar
                                onOpenCityModal={(pendingSearch) => {
                                    setPendingSearchAfterCity(pendingSearch ?? null);
                                    prepareShellModals();
                                    setCityModalOpen(true);
                                }}
                            />
                        </Box>
                    </Box>
                )}

                {/* Right: search (md+) + sign up + inbox + menu */}
                <Box
                    sx={{
                        gridColumn: { xs: '3', md: '3' },
                        gridRow: { xs: '1', md: '1' },
                        justifySelf: 'end',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: { xs: 0.25, sm: 0.5, md: 1 },
                        alignItems: 'center',
                        minWidth: 0,
                        flexWrap: 'nowrap',
                        overflow: 'visible',
                    }}
                >
                    {showInlineSearch && (
                        <Box
                            sx={{
                                maxWidth: { md: 300, lg: 350 },
                                minWidth: { md: 200, lg: 260 },
                                width: { md: 'min(36vw, 350px)' },
                                flexShrink: 1,
                            }}
                        >
                            <SearchBar
                                onOpenCityModal={(pendingSearch) => {
                                    setPendingSearchAfterCity(pendingSearch ?? null);
                                    prepareShellModals();
                                    setCityModalOpen(true);
                                }}
                            />
                        </Box>
                    )}

                    {!auth.isLogin() && (
                        <Button
                            variant={isNarrowViewport ? 'contained' : 'outlined'}
                            size="small"
                            onClick={showAuthOptions}
                            aria-haspopup="dialog"
                            sx={{
                                flexShrink: 0,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: responsiveFontSize.bodyFixed,
                                px: { xs: 1.25, sm: 1.5 },
                                py: 0.5,
                                minHeight: 36,
                                ...(isNarrowViewport
                                    ? {
                                        backgroundColor: getButtonColor(theme),
                                        color: theme.palette.getContrastText(getButtonColor(theme)),
                                        boxShadow: 'none',
                                        '@media (hover: hover)': {
                                            '&:hover': {
                                                backgroundColor: getButtonHoverColor(theme),
                                                boxShadow: 'none',
                                            },
                                        },
                                    }
                                    : {
                                        borderColor: alpha(getButtonColor(theme), 0.45),
                                        color: getButtonColor(theme),
                                        '@media (hover: hover)': {
                                            '&:hover': {
                                                borderColor: getButtonColor(theme),
                                                backgroundColor: alpha(getButtonColor(theme), 0.06),
                                            },
                                        },
                                    }),
                            }}
                        >
                            Sign up
                        </Button>
                    )}

                    {auth.isLogin() && <NotificationIcon onClick={onClickNotifications} />}

                    <IconButton
                        onClick={handleHamburgerMenuOpen}
                        sx={{
                            p: { xs: 0.5, sm: 1 },
                            flexShrink: 0,
                            minWidth: 40,
                            minHeight: 40,
                            display: 'flex !important',
                            visibility: 'visible !important',
                            opacity: '1 !important',
                        }}
                        aria-label="Menu"
                    >
                        <MenuRoundedIcon
                            sx={{
                                fontSize: responsiveIconSize.md,
                                display: 'block',
                            }}
                        />
                    </IconButton>
                </Box>
            </Box>

            {/* Hamburger Menu Content */}
            <Menu
                anchorEl={hamburgerMenuAnchor}
                open={Boolean(hamburgerMenuAnchor)}
                onClose={closeAllMenus}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        backdropFilter: 'blur(4px)',
                    }
                }}
                sx={{ 
                    zIndex: Z_INDEX.MENU_MAIN
                }}
                PaperProps={{
                    sx: {
                        minWidth: { xs: 270, sm: 310 },
                        mt: 1,
                        maxHeight: '80vh',
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        borderRadius: { xs: 2, sm: 3 },
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.4 : 0.12)}`,
                        opacity: Boolean(subMenuAnchor) ? 0.15 : 1,
                        transition: 'opacity 0.2s ease-in-out',
                        pointerEvents: Boolean(subMenuAnchor) ? 'none' : 'auto',
                        p: 0,
                    }
                }}
            >
                {auth.isLogin() ? (
                    <Box component="div" sx={{ py: 0 }}>
                        {/* User Profile Section - Gradient header */}
                        <Box sx={{ 
                            p: { xs: 1.5, sm: 2 },
                            pb: { xs: 1.5, sm: 1.5 },
                            background: `linear-gradient(160deg, ${alpha(getButtonColor(theme), 0.06)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 50%, transparent 100%)`,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            textAlign: 'center'
                        }}>
                            <Avatar
                                src={profilePicture || undefined}
                                sx={{ 
                                    width: { xs: 48, sm: 56 }, 
                                    height: { xs: 48, sm: 56 },
                                    bgcolor: getButtonColor(theme),
                                    mx: 'auto',
                                    mb: 0.75,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                                }}
                            >
                                {!profilePicture && <AccountCircleIcon sx={{ fontSize: responsiveIconSize.icon24_28, color: '#fff' }} />}
                            </Avatar>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: responsiveFontSize.bodySubtitle }}>
                                {auth.user?.firstName} {auth.user?.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: responsiveFontSize.subtext }}>
                                {userType === 'customer' ? 'Customer' : userType === 'worker' ? 'Expert' : userType === 'admin' ? 'Admin' : userType === 'manager' ? 'Manager' : 'User'}
                            </Typography>
                        </Box>

                        {(userType === 'admin' || userType === 'manager' || userType === null) && <ColorSchemeMenuToggle />}

                        {/* Menu Items based on user type */}
                        {userType === 'customer' ? (
                            <Box>
                                <DashboardMenuItem theme={theme} icon={<ShoppingCartIcon />} text="Create Order" onClick={() => handleMenuItemClick(onClickCustomerCreateOrder)} />
                                <DashboardMenuItem theme={theme} icon={<HistoryIcon />} text="Order History" onClick={() => handleMenuItemClick(onClickCustomerOrderHistory)} />
                                <DashboardMenuItem theme={theme} icon={<CategoryIcon />} text="Services" onClick={(e: any) => handleSubMenuClick(e, 'Services')} hasArrow />
                                <DashboardMenuItem theme={theme} icon={<HandymanIcon />} text="Experts in your city" onClick={() => handleMenuItemClick(onClickFindWorkers)} />
                                <DashboardMenuItem theme={theme} icon={<AccountCircleIcon />} text="My Dashboard" onClick={() => handleMenuItemClick(onClickCustomerDashboard)} />
                                <ColorSchemeMenuToggle />
                                <DashboardMenuItem theme={theme} icon={<LogoutIcon />} text="Log out" onClick={() => handleMenuItemClick(onClickLogout)} isLogout showDivider={false} />
                            </Box>
                        ) : userType === 'admin' ? (
                            <Box>
                                <DashboardMenuItem theme={theme} icon={<DashboardIcon />} text="Admin Dashboard" onClick={() => handleMenuItemClick(onClickAdminDashboard)} />
                                <DashboardMenuItem theme={theme} icon={<AccountCircleIcon />} text="Profile Settings" onClick={() => handleMenuItemClick(onClickAdminProfile)} />
                                <DashboardMenuItem theme={theme} icon={<LogoutIcon />} text="Log out" onClick={() => handleMenuItemClick(onClickLogout)} isLogout showDivider={false} />
                            </Box>
                        ) : userType === 'manager' ? (
                            <Box>
                                <DashboardMenuItem theme={theme} icon={<DashboardIcon />} text="Manager Dashboard" onClick={() => handleMenuItemClick(onClickManagerDashboard)} />
                                <DashboardMenuItem theme={theme} icon={<AccountCircleIcon />} text="Profile Settings" onClick={() => handleMenuItemClick(onClickManagerProfile)} />
                                <DashboardMenuItem theme={theme} icon={<LogoutIcon />} text="Log out" onClick={() => handleMenuItemClick(onClickLogout)} isLogout showDivider={false} />
                            </Box>
                        ) : userType === 'worker' ? (
                            <Box>
                                <DashboardMenuItem theme={theme} icon={<WorkIcon />} text="Find Available Jobs" onClick={() => handleMenuItemClick(onClickAvailableProjects)} />
                                <DashboardMenuItem theme={theme} icon={<HistoryIcon />} text="Job History" onClick={() => handleMenuItemClick(onClickWorkerJobHistory)} />
                                <DashboardMenuItem theme={theme} icon={<AccountCircleIcon />} text="My Dashboard" onClick={() => handleMenuItemClick(onClickWorkerDashboard)} />
                                <ColorSchemeMenuToggle />
                                <DashboardMenuItem theme={theme} icon={<LogoutIcon />} text="Log out" onClick={() => handleMenuItemClick(onClickLogout)} isLogout showDivider={false} />
                            </Box>
                        ) : null}
                    </Box>
                ) : (
                    <Box component="div" sx={{ py: 0 }}>
                        <DashboardMenuItem theme={theme} icon={<LoginIcon />} text="Login or Signup" onClick={showAuthOptions} isProminent />
                        <DashboardMenuItem theme={theme} icon={<CategoryIcon />} text="Services" onClick={(e: any) => handleSubMenuClick(e, 'Services')} hasArrow />
                        <DashboardMenuItem theme={theme} icon={<HandymanIcon />} text="Experts in your city" onClick={() => handleMenuItemClick(onClickFindWorkers)} />
                        <DashboardMenuItem theme={theme} icon={<DescriptionIcon />} text="Terms and Conditions" onClick={() => handleMenuItemClick(onClickTerms)} />
                        <ColorSchemeMenuToggle />
                    </Box>
                )}
            </Menu>

            <React.Suspense fallback={null}>
                <ServicesMenu
                    anchorEl={subMenuAnchor}
                    open={Boolean(subMenuAnchor)}
                    onClose={closeAllMenus}
                    onOpenThirdLevelMenu={handleThirdLevelMenuOpen}
                    onClickServices={onClickServices}
                    setActiveService={setActiveService}
                    closeAllMenus={closeAllMenus}
                    isThirdLevelOpen={Boolean(thirdLevelMenuAnchor)}
                    onBack={handleCloseServicesMenuOnly}
                />
                <ThirdLevelMenu
                    anchorEl={thirdLevelMenuAnchor}
                    open={Boolean(thirdLevelMenuAnchor)}
                    onClose={closeAllMenus}
                    activeService={activeService}
                    closeAllMenus={closeAllMenus}
                    onBack={handleCloseThirdLevelOnly}
                />
            </React.Suspense>

            {/* City Selection Modal (e.g. when user searches without location) */}
            {shellModalsReady && (
                <React.Suspense fallback={null}>
                    <CitySelectionModal
                        open={cityModalOpen}
                        onClose={handleCityModalClose}
                        onCitySelected={handleCitySelected}
                        bannerMessage={pendingSearchAfterCity ? 'Please select your city to see your search results.' : undefined}
                    />

                    <GetStartedChooser
                        open={getStartedOpen}
                        onClose={() => setGetStartedOpen(false)}
                        onSelect={(type) => {
                            setGetStartedOpen(false);
                            handleAuthSelection(type);
                        }}
                    />

                    {/* Auth Modal */}
                    {userType && (
                        <AuthModal
                            open={authModalOpen}
                            onClose={() => {
                                setAuthModalOpen(false);
                                // Reset modal state when closing
                                setAuthModalMode('signup');
                                setAuthModalLoginOnly(false);
                                setAuthModalExpectedUserId(undefined);
                                setAuthModalCustomMessage(undefined);
                            }}
                            userType={userType}
                            initialMode={authModalMode}
                            loginOnly={authModalLoginOnly}
                            expectedUserId={authModalExpectedUserId}
                            customMessage={authModalCustomMessage}
                        />
                    )}
                </React.Suspense>
            )}

            {needHelpModals}
        </Box>
    );
}